import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { subscribeEbaySyncProgress } from "../../services/batchSocket";
import {
  formatSyncRunStatus,
  getApiBaseUrl,
  getEbaySyncRun,
  resolveMediaUrl,
} from "../../services/ebaySync";
import { EBAY_SYNC } from "../../routes/routes";
import EbaySyncProgressBar from "./EbaySyncProgressBar";
import "./ebay-sync.css";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function statusBadgeClass(status) {
  return `ebay-sync__badge ebay-sync__badge--${status || "unknown"}`;
}

function SyncCardItem({ item, apiBaseUrl, showError = false }) {
  const imageUrl = resolveMediaUrl(item.front_image, apiBaseUrl);

  return (
    <div className="ebay-sync-detail__card">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={item.title || `Card ${item.card_id}`}
          className="ebay-sync-detail__card-img"
          loading="lazy"
        />
      ) : (
        <div className="ebay-sync-detail__card-img" />
      )}
      <div className="ebay-sync-detail__card-info">
        <p className="ebay-sync-detail__card-title">
          {item.title || `Card #${item.card_id}`}
        </p>
        <p className="ebay-sync-detail__card-meta">
          ID: {item.card_id}
          {item.sku ? ` · SKU: ${item.sku}` : ""}
          {item.ebay_offer_id ? ` · Offer: ${item.ebay_offer_id}` : ""}
        </p>
        {item.processing_phase && (
          <p className="ebay-sync-detail__card-meta">
            Phase: {item.processing_phase}
          </p>
        )}
        {showError && item.error && (
          <p className="ebay-sync-detail__card-error">
            {item.failed_step ? `[${item.failed_step}] ` : ""}
            {item.error}
          </p>
        )}
        {showError && item.error_details && (
          <details className="ebay-sync-detail__error-details">
            <summary>Show details</summary>
            <pre>{JSON.stringify(item.error_details, null, 2)}</pre>
          </details>
        )}
      </div>
    </div>
  );
}

function DetailColumn({ title, items, apiBaseUrl, showError = false }) {
  return (
    <div className="ebay-sync-detail__column">
      <div className="ebay-sync-detail__column-header">
        {title} ({items.length})
      </div>
      <div className="ebay-sync-detail__column-body">
        {items.length === 0 ? (
          <div className="ebay-sync-detail__empty">No cards</div>
        ) : (
          items.map((item) => (
            <SyncCardItem
              key={`${item.card_id}-${item.processing_phase || title}`}
              item={item}
              apiBaseUrl={apiBaseUrl}
              showError={showError}
            />
          ))
        )}
      </div>
    </div>
  );
}

function EbaySyncTaskDetail({ syncRunId: syncRunIdProp, onClose, isModal = false }) {
  const params = useParams();
  const syncRunId = syncRunIdProp ?? params.syncRunId;
  const apiBaseUrl = getApiBaseUrl();
  const [run, setRun] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchRun = useCallback(async () => {
    if (!syncRunId) return null;
    try {
      const data = await getEbaySyncRun(syncRunId, apiBaseUrl);
      setRun(data);
      setError("");
      return data;
    } catch (err) {
      setError(err?.message || "Failed to load sync task");
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [syncRunId, apiBaseUrl]);

  useEffect(() => {
    setIsLoading(true);
    fetchRun();
  }, [fetchRun]);

  useEffect(() => {
    if (!syncRunId) return undefined;

    const unsubscribe = subscribeEbaySyncProgress(
      {
        syncRunId,
        handlers: {
          onProgress: (payload) => {
            setRun((prev) => {
              if (!prev) return prev;
              return {
                ...prev,
                status: payload.completed ? payload.status : prev.status,
                counts: payload.counts ?? prev.counts,
                summary: payload.summary ?? prev.summary,
              };
            });
          },
          onComplete: () => {
            fetchRun();
          },
        },
      },
      apiBaseUrl
    );

    return unsubscribe;
  }, [syncRunId, apiBaseUrl, fetchRun]);

  useEffect(() => {
    if (!run || run.status !== "running") return undefined;

    const interval = setInterval(fetchRun, 2500);
    return () => clearInterval(interval);
  }, [run?.status, fetchRun]);

  if (isLoading && !run) {
    return (
      <div className="ebay-sync">
        <div className="ebay-sync__loading">Loading sync task...</div>
      </div>
    );
  }

  if (error && !run) {
    return (
      <div className="ebay-sync">
        {isModal ? (
          <button type="button" className="ebay-sync__back" onClick={onClose}>
            <ArrowLeft size={16} />
            Close
          </button>
        ) : (
          <Link to={EBAY_SYNC} className="ebay-sync__back">
            <ArrowLeft size={16} />
            Back to tasks
          </Link>
        )}
        <div className="ebay-sync__error" style={{ marginTop: "1rem" }}>
          {error}
        </div>
      </div>
    );
  }

  const details = run?.details || {};
  const summary = run?.summary || {};
  const counts = run?.counts || {};

  return (
    <div className="ebay-sync">
      {isModal ? (
        <button type="button" className="ebay-sync__back" onClick={onClose}>
          <ArrowLeft size={16} />
          Close
        </button>
      ) : (
        <Link to={EBAY_SYNC} className="ebay-sync__back">
          <ArrowLeft size={16} />
          Back to tasks
        </Link>
      )}

      <div className="ebay-sync__header" style={{ marginTop: "1rem" }}>
        <h2 className="ebay-sync__title">
          Sync Task #{run.id}
          {run.batch_name ? ` · ${run.batch_name}` : ""}
        </h2>
        <span className={statusBadgeClass(run.status)}>
          {formatSyncRunStatus(run.status)}
        </span>
      </div>

      {run.error_message && (
        <div className="ebay-sync__error">{run.error_message}</div>
      )}

      <div className="ebay-sync-detail__meta">
        <div className="ebay-sync-detail__meta-card">
          <div className="ebay-sync-detail__meta-label">Batch</div>
          <div className="ebay-sync-detail__meta-value">
            {run.batch_name || run.batch_id || "—"}
          </div>
        </div>
        <div className="ebay-sync-detail__meta-card">
          <div className="ebay-sync-detail__meta-label">Started</div>
          <div className="ebay-sync-detail__meta-value">
            {formatDate(run.started_at)}
          </div>
        </div>
        <div className="ebay-sync-detail__meta-card">
          <div className="ebay-sync-detail__meta-label">Completed</div>
          <div className="ebay-sync-detail__meta-value">
            {formatDate(run.completed_at)}
          </div>
        </div>
        <div className="ebay-sync-detail__meta-card">
          <div className="ebay-sync-detail__meta-label">Requested</div>
          <div className="ebay-sync-detail__meta-value">
            {summary.requested ?? counts.total ?? "—"}
          </div>
        </div>
        <div className="ebay-sync-detail__meta-card">
          <div className="ebay-sync-detail__meta-label">Succeeded</div>
          <div className="ebay-sync-detail__meta-value">
            {run.status === "running"
              ? counts.listed + (counts.already_listed || 0)
              : summary.succeeded ?? "—"}
          </div>
        </div>
        <div className="ebay-sync-detail__meta-card">
          <div className="ebay-sync-detail__meta-label">Newly listed</div>
          <div className="ebay-sync-detail__meta-value">
            {run.status === "running" ? "—" : summary.newly_listed ?? "—"}
          </div>
        </div>
        <div className="ebay-sync-detail__meta-card">
          <div className="ebay-sync-detail__meta-label">Already listed</div>
          <div className="ebay-sync-detail__meta-value">
            {summary.already_listed ?? counts.already_listed ?? "—"}
          </div>
        </div>
        <div className="ebay-sync-detail__meta-card">
          <div className="ebay-sync-detail__meta-label">Failed</div>
          <div className="ebay-sync-detail__meta-value">
            {summary.failed ?? counts.failed ?? 0}
          </div>
        </div>
      </div>

      <EbaySyncProgressBar counts={counts} status={run.status} />

      <div className="ebay-sync-detail__columns">
        <DetailColumn
          title="In queue"
          items={details.in_queue || []}
          apiBaseUrl={apiBaseUrl}
        />
        <DetailColumn
          title="Listing"
          items={details.listing || []}
          apiBaseUrl={apiBaseUrl}
        />
        <DetailColumn
          title="Listed"
          items={details.listed || []}
          apiBaseUrl={apiBaseUrl}
        />
        <DetailColumn
          title="Failed"
          items={details.failed || []}
          apiBaseUrl={apiBaseUrl}
          showError
        />
      </div>
    </div>
  );
}

export default EbaySyncTaskDetail;
